import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../mendepartmentdetails/Sidebar';
import SubDeptImage from '../../../assets/image/department/driving-test.jpg';

function DrivingTestsDetails() {
  const [Faq, setFaq] = useState(0); // first dropdown open by default
  const titleRef = useRef(null);
  const [titleColor, setTitleColor] = useState('');

  useEffect(() => {
    if (titleRef.current) {
      const c = window.getComputedStyle(titleRef.current).color;
      setTitleColor(c);
    }
  }, []);

  // shared inline styles (consistent with other extended pages)
  const liRow = { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' };
  const icon = { marginTop: '4px', flex: '0 0 auto' };
  const kv = { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 };
  const keyText = { fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.35, color: '#0f172a' };
  const valText = { fontSize: '1rem', lineHeight: 1.6, color: '#334155' };
  const overviewText = { color: titleColor || '#0f172a', fontSize: '1.05rem', lineHeight: 1.6, margin: '8px 0 16px' };

  const sections = [
    {
      Que: 'Scheduling & Eligibility',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Who must test:', 'New drivers, upgrades (e.g., adding endorsements), and reinstatements may require knowledge and/or road tests.'],
            ['Appointments:', 'Schedule in advance online or by phone; limited walk-in capacity at some centers.'],
            ['Prerequisites:', 'Hold required permits, complete any classroom/behind-the-wheel training if applicable, and meet age/residency rules.'],
          ].map(([k, v], i) => (
            <li key={`sched-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Written Knowledge Exam',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Format & topics:', 'Multiple-choice on rules of the road, signs, safe driving practices, and penalties.'],
            ['Study materials:', 'Use the current driver manual and practice tests. Bring valid ID to the exam.'],
            ['Retests:', 'Retest waiting periods and additional fees may apply after a failed attempt.'],
          ].map(([k, v], i) => (
            <li key={`written-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Road Test (Behind-the-Wheel)',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Skills assessed:', 'Starts/stops, turning, lane changes, speed control, scanning, parking, and right-of-way decisions.'],
            ['Automatic fails:', 'Disobeying signals/signs, speeding excessively, unsafe lane changes, collisions/near-misses, or examiner intervention.'],
            ['Readiness:', 'Practice defensive driving, mirror checks, head turns, and safe following distance.'],
          ].map(([k, v], i) => (
            <li key={`road-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Vehicle Requirements for the Road Test',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Condition & equipment:', 'Street-legal car with working lights, horn, wipers, mirrors, seatbelts, and windshield free of major cracks.'],
            ['Documents:', 'Valid registration and insurance; inspection/emissions proof if required.'],
            ['Clean & safe:', 'No loose items that could move during braking; tires properly inflated.'],
          ].map(([k, v], i) => (
            <li key={`veh-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Minors: Supervised Driving & Logs',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Practice hours:', 'Some applicants must show supervised driving hours—keep a signed log if required.'],
            ['Curfews & passengers:', 'Youth driving restrictions may apply; review limits before testing.'],
            ['Parent/guardian presence:', 'A parent/guardian may need to be present for consent at check-in.'],
          ].map(([k, v], i) => (
            <li key={`minors-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'What to Bring on Test Day',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Identification:', 'Permit or prior license, and another acceptable ID if required.'],
            ['Proofs & paperwork:', 'Insurance, registration, completion certificates (if any), and appointment confirmation.'],
            ['Payment:', 'Fees for testing/issuance; confirm accepted payment types at your center.'],
          ].map(([k, v], i) => (
            <li key={`bring-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Accommodations & Language Access',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Testing accommodations:', 'Request in advance for disability-related needs; provide supporting documentation if required.'],
            ['Languages & interpreters:', 'Availability varies by center; ask about language options before scheduling.'],
            ['Alternate formats:', 'Large print or other formats may be available upon request.'],
          ].map(([k, v], i) => (
            <li key={`access-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Rescheduling, Cancellations & Weather',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Change your appointment:', 'Modify online or call; no-shows may forfeit fees.'],
            ['Arrive early:', 'Late arrivals may be asked to reschedule.'],
            ['Weather impacts:', 'Severe weather can pause or cancel road tests; check alerts before you leave.'],
          ].map(([k, v], i) => (
            <li key={`resched-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Fees & Retest Policies',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Exam fees:', 'Knowledge and road test fees are collected per attempt in many cases.'],
            ['Retest wait:', 'A waiting period may apply after failures; verify center-specific rules.'],
            ['Issuance costs:', 'License issuance/printing fees assessed upon passing.'],
          ].map(([k, v], i) => (
            <li key={`fees-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'After You Test',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['If you pass:', 'You may receive an interim document; your permanent card is mailed when processed.'],
            ['If you do not pass:', 'Review the feedback, schedule a retest, and continue practicing focus areas.'],
            ['Record updates:', 'Confirm your name, address, and restrictions are correctly listed.'],
          ].map(([k, v], i) => (
            <li key={`after-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
  ];

  return (
    <section className="department-details-section">
      <div className="container">
        <div className="row">

          {/* Sidebar */}
          <div className="col-12 col-lg-4 col-xl-4">
            <Sidebar />
          </div>

          {/* Main Content */}
          <div className="col-lg-8">
            <div className="department-details-imgbox">
              <img src={SubDeptImage} className="img-fluid" alt="Driving Tests" />
            </div>

            <div className="department-details-content-box">
              <h4 ref={titleRef} className="department-details-title">Driving Tests</h4>
              {/* Overview (always visible, same color as title but smaller) */}
              <p style={overviewText}>
                Knowledge and road tests confirm you can operate safely. Schedule early, bring required documents,
                ensure your vehicle is road-ready, and practice core skills to pass on the first try.
              </p>
            </div>

            {/* Accordion (expanded information) */}
            <div className="accordian-box">
              {sections.map((item, index) => (
                <div className={`accordian-box-item ${Faq !== index && 'active'}`} key={index}>
                  <div className="accordian-title">
                    <h5>{item.Que}</h5>
                    <i
                      className="fa-solid fa-angle-right"
                      onClick={() => setFaq(index)}
                      role="button"
                      aria-label={`Toggle ${item.Que}`}
                    />
                  </div>

                  {Faq === index && (
                    <div className="accordian-content" style={{ display: 'block' }}>
                      {item.Answer}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="department-details-policy-box">
              <h4>Our Commitment:</h4>
              <p>
                We provide clear guidance, fair testing, and prompt results—so qualified drivers can get licensed and
                road-ready with confidence.
              </p>
            </div>

            <div>
              <Link to="/department/dmv">
                <i className="fa-solid fa-circle-arrow-left" style={{ marginRight: '5px' }} />
                Go Back
              </Link>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}

export default DrivingTestsDetails;
